/**
 * ProcedureInputField - Input component for cpt_code array field
 *
 * Manages a list of procedure codes (CPT codes) with add/remove functionality.
 *
 * Exports:
 * - ProcedureInputField: Main input component
 * - ProcedureInputFieldPreview: Preview component for table cells
 */

import React from 'react';

import type { CodeInputFieldConfig, CodeItem } from './CodeInputField';
import { CodeInputField } from './CodeInputField';
import { SchemaProvider, useSchemaContextSafe } from './context/SchemaContext';
import { PillPreviewList, type PreviewPill } from './PillPreviewList';
import type { InputArrayProps, PreviewProps } from './types';

// Schema field id used by the registry for this component
// (see fields/index.ts inputComponents map).
const SCHEMA_FIELD_ID = 'cpt_code';
const CODE_SUBFIELD_ID = 'code';

// =============================================================================
// MAIN INPUT COMPONENT
// =============================================================================

const PROCEDURE_CONFIG: CodeInputFieldConfig = {
  label: 'CPT Code',
  placeholder: 'e.g., 99213',
  emptyMessage: 'No procedures added',
  addButtonLabel: 'Add CPT Code',
  deleteTitle: 'Delete Procedure?',
  deleteDescription:
    'This action cannot be undone. This will permanently delete this procedure entry.',
  itemLabelPrefix: 'Procedure',
};

/**
 * Public component — mounts SchemaProvider so the inner body can consume
 * `isFieldRequired` from context. The inner body MUST be a separate component;
 * calling useSchemaContextSafe() in the same component that renders
 * <SchemaProvider> would see the outer (missing) context, not the one it
 * just installed.
 */
export const ProcedureInputField: React.FC<InputArrayProps> = props => (
  <SchemaProvider>
    <ProcedureInputFieldInner {...props} />
  </SchemaProvider>
);

const ProcedureInputFieldInner: React.FC<InputArrayProps> = ({
  data,
  onChange,
  errors = {},
  disabled = false,
}) => {
  // Drive the `*` indicator from the schema. CodeInputField stays
  // schema-agnostic; we pass the resolved boolean in via config.
  const { isFieldRequired } = useSchemaContextSafe();
  const config: CodeInputFieldConfig = {
    ...PROCEDURE_CONFIG,
    required: isFieldRequired(CODE_SUBFIELD_ID, SCHEMA_FIELD_ID),
  };

  // CodeInputField expects CodeItem[] - cast from unknown[]
  return (
    <CodeInputField
      data={data as CodeItem[]}
      onChange={onChange as (data: CodeItem[]) => void}
      errors={errors}
      disabled={disabled}
      config={config}
    />
  );
};

// =============================================================================
// PREVIEW COMPONENT
// =============================================================================

/** Item shape for preview (defined locally, schema provides field definitions at runtime) */
interface ProcedureItem {
  code?: string;
}

/**
 * ProcedureInputFieldPreview - Pill-based preview component for cpt_code array field
 *
 * Displays procedures as pills with format: code (monospace)
 */
export const ProcedureInputFieldPreview: React.FC<PreviewProps> = ({ data, maxItems = 2 }) => {
  const items = (Array.isArray(data) ? data : []) as ProcedureItem[];

  // `key` need not be unique — PillPreviewList appends the index.
  const pills: PreviewPill[] = items.flatMap(item =>
    item.code ? [{ key: item.code, text: item.code, className: 'font-mono' }] : [],
  );

  return <PillPreviewList pills={pills} maxItems={maxItems} />;
};
